// Blog articles for EV Hub Studio
// Each section type: p | h2 | h3 | ul | ol | table | callout | cta | image-grid
// hero: { gradient: [from, to], icon: "lucide-icon-name", stat: "₹5L–₹2.5Cr", statLabel: "CAPEX range" }

export const blogPosts = [
  {
    slug: "ev-charging-station-cost-india-2026",
    title: "EV Charging Station Cost in India 2026: Complete CAPEX Breakdown",
    description:
      "Planning to set up an EV charging station in India? This guide covers equipment costs, civil works, electrical infrastructure, solar PV, and total CAPEX ranges for AC and DC charging setups.",
    date: "2026-05-15",
    readTime: "8 min read",
    category: "CAPEX & Finance",
    tags: ["CAPEX", "Cost", "AC Charger", "DC Charger", "India"],
    hero: {
      image: "/blog/blog-ev-charging-cost.jpg",
      gradient: ["#1a3d35", "#0d2420"],
      icon: "IndianRupee",
      stat: "₹5L – ₹2.5Cr",
      statLabel: "Typical CAPEX range",
      chips: ["AC Charger", "DC Fast Charger", "Civil Works", "Solar PV"],
    },
    sections: [
      {
        type: "p",
        content:
          "Setting up an EV charging station in India can cost anywhere from <strong>₹5 lakh for a single AC charger</strong> to over <strong>₹2.5 crore for a large multi-bay DC fast-charging hub</strong>. The wide range makes accurate upfront planning essential — overestimating kills project viability, while underestimating leads to mid-project cash crunches. This guide breaks down every cost component so you can build a realistic CAPEX estimate before committing capital.",
      },
      {
        type: "h2",
        text: "Types of EV Chargers and Their Equipment Cost",
      },
      {
        type: "p",
        content:
          "Charger selection is the single largest cost driver. India's market has three practical tiers:",
      },
      {
        type: "table",
        headers: ["Charger Type", "Power (kW)", "Standard", "Cost per Unit (₹)"],
        rows: [
          ["AC Slow Charger", "3.3 – 7.4 kW", "IEC 62196 Type 2", "₹40,000 – ₹80,000"],
          ["AC Fast Charger", "11 – 22 kW", "IEC 62196 Type 2", "₹1.2L – ₹2.5L"],
          ["DC Fast Charger", "30 – 60 kW", "CCS2 / CHAdeMO", "₹4L – ₹8L"],
          ["DC Rapid Charger", "120 – 180 kW", "CCS2", "₹15L – ₹25L"],
          ["DC Ultra-Rapid", "240 – 360 kW", "CCS2", "₹30L – ₹45L"],
        ],
      },
      {
        type: "callout",
        content:
          "<strong>Tip:</strong> For highway corridors, 120 kW DC chargers offer the best balance of speed (0–80% in ~40 min for most EVs) and cost. 30 kW units are better suited for captive fleets or residential complexes where dwell time is longer.",
      },
      {
        type: "h2",
        text: "Civil and Electrical Infrastructure Costs",
      },
      {
        type: "p",
        content:
          "Equipment is only 40–60% of total CAPEX. The rest goes into site preparation and electrical work:",
      },
      {
        type: "ul",
        items: [
          "<strong>DISCOM transformer / load sanction:</strong> ₹2L – ₹12L depending on connected load (15–200 kVA). Higher-load sites pay more for HT connection charges.",
          "<strong>Internal HT/LT cabling:</strong> ₹1.5L – ₹6L based on cable runs and trench length.",
          "<strong>Civil works (flooring, canopy, signage, drainage):</strong> ₹3L – ₹15L depending on site area and finishes.",
          "<strong>Earthing and lightning protection:</strong> ₹50,000 – ₹1.5L.",
          "<strong>SCADA / energy meter / billing system:</strong> ₹80,000 – ₹2L.",
          "<strong>Installation and commissioning:</strong> ₹50,000 – ₹3L.",
        ],
      },
      {
        type: "h2",
        text: "Optional Add-Ons",
      },
      {
        type: "ul",
        items: [
          "<strong>Solar PV canopy (10–50 kWp):</strong> ₹5L – ₹22L. Reduces per-unit energy cost and qualifies for additional FAME/state subsidies.",
          "<strong>Battery energy storage system (BESS):</strong> ₹8L – ₹40L. Useful for ToD tariff arbitrage and reducing peak demand charges.",
          "<strong>CCTV and access control:</strong> ₹80,000 – ₹2L.",
          "<strong>EV fleet management software / white-label app:</strong> ₹1L – ₹5L (one-time) + SaaS fees.",
        ],
      },
      {
        type: "h2",
        text: "Total CAPEX Ranges by Station Type",
      },
      {
        type: "table",
        headers: ["Station Type", "Chargers", "Typical Total CAPEX"],
        rows: [
          ["Single AC Charger (residential/office)", "1 × 7.4 kW AC", "₹5L – ₹8L"],
          ["Small DC Hub (retail/parking)", "2 × 30 kW DC", "₹20L – ₹35L"],
          ["Mid-size Highway Stop", "2 × 60 kW + 2 × 30 kW", "₹40L – ₹70L"],
          ["Large Highway Hub", "4 × 120 kW DC", "₹1.2Cr – ₹1.8Cr"],
          ["Multi-bay Charging Plaza", "8 × 120 kW + 4 × 30 kW", "₹2Cr – ₹2.8Cr"],
        ],
      },
      {
        type: "h2",
        text: "Hidden Costs Most Developers Miss",
      },
      {
        type: "ol",
        items: [
          "<strong>Power factor penalty:</strong> DISCOMs charge penalty for PF < 0.9. Budget for PF correction capacitors (₹50K–₹2L).",
          "<strong>DISCOM demand charges:</strong> If connected load > 50 kW, monthly fixed demand charges apply regardless of utilisation — factor this into your cash flow model.",
          "<strong>Land lease or parking revenue share:</strong> Fuel station, mall, or highway authority partnerships often take 15–25% of revenue.",
          "<strong>OCPP backend subscription:</strong> ₹500–₹2,000 per port per month for cloud charging management.",
          "<strong>Insurance:</strong> ₹30,000–₹1.5L annually for equipment and liability coverage.",
        ],
      },
      {
        type: "cta",
        text: "Get an instant, site-specific CAPEX breakdown — plug in your charger mix, site state, and load requirements.",
        label: "Use the Free CAPEX Estimator →",
        href: "/try",
      },
    ],
  },

  {
    slug: "fame-iii-subsidy-ev-charging-station-india",
    title: "FAME III Subsidy for EV Charging Stations in India: Eligibility & How to Apply",
    description:
      "India's FAME III scheme provides capital subsidies for EV charging infrastructure. Learn eligibility criteria, subsidy amounts, state-level schemes, and the step-by-step application process.",
    date: "2026-05-15",
    readTime: "7 min read",
    category: "Subsidies & Finance",
    tags: ["FAME III", "Subsidy", "Government Scheme", "EV Policy", "India"],
    hero: {
      image: "/blog/blog-fame-iii-subsidy.jpg",
      gradient: ["#3a2e28", "#1e1710"],
      icon: "Award",
      stat: "Up to ₹50L",
      statLabel: "Per station central grant",
      chips: ["FAME III", "State Subsidies", "SIDBI / IREDA", "DHI Portal"],
    },
    sections: [
      {
        type: "p",
        content:
          "India's <strong>FAME III (Faster Adoption and Manufacturing of Electric Vehicles)</strong> programme is the central government's primary demand-incentive scheme for EV adoption and charging infrastructure. Building on FAME II (which disbursed over ₹10,000 crore between 2019–2024), FAME III significantly expands the scope for public charging infrastructure and fast-charging corridors — making it a critical funding lever for anyone building a commercial EV charging station today.",
      },
      {
        type: "h2",
        text: "What FAME III Covers for Charging Infrastructure",
      },
      {
        type: "ul",
        items: [
          "<strong>Public fast-charging stations</strong> on national highways, expressways, and state highways — capital subsidy per charger.",
          "<strong>City charging hubs</strong> in Tier 1 and Tier 2 cities — subsidised DC fast chargers for commercial operators.",
          "<strong>Fleet charging</strong> at bus depots, logistics parks, and last-mile delivery hubs.",
          "<strong>Destination charging</strong> at hotels, malls, and commercial complexes (AC and DC).",
          "<strong>Residential charging</strong> in housing societies under specific state co-schemes.",
        ],
      },
      {
        type: "h2",
        text: "Subsidy Amounts (Central Government)",
      },
      {
        type: "table",
        headers: ["Charger Type", "Subsidy per Unit", "Cap (per station)"],
        rows: [
          ["AC Slow/Fast Charger (≤22 kW)", "25% of equipment cost", "₹50,000"],
          ["DC Fast Charger (30–60 kW)", "25% of equipment cost", "₹2L per charger"],
          ["DC Rapid Charger (≥100 kW)", "15% of equipment cost", "₹5L per charger"],
          ["Highway Fast Charging Station", "Fixed grant", "₹10L – ₹50L (location-based)"],
        ],
      },
      {
        type: "callout",
        content:
          "<strong>Note:</strong> Subsidy amounts are indicative and subject to notification updates. Always verify the latest figures on the BEE/DHI portal before finalising your project finance model.",
      },
      {
        type: "h2",
        text: "Eligibility Criteria",
      },
      {
        type: "ul",
        items: [
          "Entity must be a registered company, LLP, or government body — individual proprietorships generally not eligible for FAME III central grants.",
          "Chargers must be BIS/AIS-138 certified and sourced from FAME-approved vendors.",
          "Chargers must be OCPP 1.6 or 2.0 compliant and connected to the national charging management system.",
          "Location must be approved by DISCOM and have the required load sanction.",
          "Operator must register on the BEE Evyatra portal.",
        ],
      },
      {
        type: "h2",
        text: "State-Level Subsidy Schemes (2026)",
      },
      {
        type: "table",
        headers: ["State", "Scheme", "Key Benefit"],
        rows: [
          ["Maharashtra", "Maharashtra EV Policy 2021–2025 (extended)", "Additional 10% capital subsidy + stamp duty waiver"],
          ["Karnataka", "Karnataka EV Policy 2021–2026", "₹1L–₹2L per DC fast charger + land at concessional rates"],
          ["Delhi", "Delhi EV Policy", "Fast-track approvals + DISCOM charges subsidy"],
          ["Gujarat", "Gujarat EV Policy", "Interest subvention on loans + capital subsidy up to ₹3L"],
          ["Tamil Nadu", "TN EV Policy 2023", "10% additional subsidy for women-led enterprises"],
          ["Rajasthan", "Rajasthan EV Policy 2022", "Electricity duty waiver for 5 years"],
        ],
      },
      {
        type: "h2",
        text: "How to Apply: Step-by-Step",
      },
      {
        type: "ol",
        items: [
          "<strong>Register on BEE Evyatra portal</strong> (evyatra.beeindia.gov.in) — create an operator account and list your proposed stations.",
          "<strong>Obtain DISCOM load sanction</strong> — this is a prerequisite for most central and state applications. Typically takes 4–12 weeks.",
          "<strong>Select FAME-approved equipment</strong> — check the approved vendor list on the DHI portal. Unapproved equipment is not eligible.",
          "<strong>Submit application on DHI portal</strong> with technical drawings, equipment invoices, DISCOM sanction letter, and entity documents.",
          "<strong>Site inspection</strong> — DHI or state agency conducts physical inspection before disbursement.",
          "<strong>Claim subsidy post-commissioning</strong> — subsidy is disbursed after site verification, usually within 60–90 days of commissioning.",
        ],
      },
      {
        type: "h2",
        text: "SIDBI and IREDA Green Financing",
      },
      {
        type: "p",
        content:
          "Beyond direct subsidies, <strong>SIDBI (Small Industries Development Bank of India)</strong> and <strong>IREDA (Indian Renewable Energy Development Agency)</strong> offer concessional loans at 8–10% p.a. for EV charging infrastructure — 150–300 bps below commercial rates. IREDA specifically targets stations with solar integration. These loans can be combined with FAME III capital subsidies, significantly improving project IRR.",
      },
      {
        type: "cta",
        text: "EV Hub Studio's subsidy finder automatically identifies central and state schemes applicable to your station location and configuration.",
        label: "Find Applicable Subsidies for Your Site →",
        href: "/planner",
      },
    ],
  },

  {
    slug: "best-dc-fast-chargers-india-2026",
    title: "Best DC Fast Chargers in India 2026: Top Vendors & Price Comparison",
    description:
      "Comparing India's leading DC fast charger vendors — ABB, Tata Power EV, Delta Electronics, Exicom, Servotech, and more. Specifications, pricing, and reliability data for commercial EV charging station operators.",
    date: "2026-05-15",
    readTime: "9 min read",
    category: "Equipment & Vendors",
    tags: ["DC Fast Charger", "Vendors", "ABB", "Tata Power", "Exicom", "Delta", "India"],
    hero: {
      image: "/blog/blog-dc-fast-chargers.jpg",
      gradient: ["#1a2d3a", "#0d1820"],
      icon: "Zap",
      stat: "30 – 360 kW",
      statLabel: "DC fast charger power range",
      chips: ["CCS2", "OCPP 2.0", "BIS Certified", "India Market"],
      images: [
        { src: "/chargers/tata.png",   label: "Tata Power EV" },
        { src: "/chargers/abb.jpg",    label: "ABB E-mobility" },
        { src: "/chargers/delta.jpg",  label: "Delta Electronics" },
        { src: "/chargers/exicom.jpg", label: "Exicom" },
      ],
    },
    sections: [
      {
        type: "p",
        content:
          "Choosing the right DC fast charger vendor is one of the most consequential decisions for a commercial EV charging station in India. A wrong choice means years of maintenance headaches, warranty disputes, and poor uptime — directly eating into your revenue. This guide compares the top DC fast charger vendors available in India in 2026 across the specifications that actually matter for operators.",
      },
      {
        type: "h2",
        text: "Key Specifications to Evaluate",
      },
      {
        type: "ul",
        items: [
          "<strong>Output power and scalability:</strong> Does the unit support power sharing between multiple cables? Can it be upgraded?",
          "<strong>Connector compatibility:</strong> CCS2 is now the Indian standard (as per AIS-138), but CHAdeMO support still matters for older EVs.",
          "<strong>OCPP compliance:</strong> OCPP 1.6J minimum; OCPP 2.0.1 preferred for advanced features like smart charging and V2G readiness.",
          "<strong>IP rating:</strong> IP54 minimum for Indian outdoor conditions; IP65 for coastal/high-humidity sites.",
          "<strong>Operating temperature range:</strong> Must handle 0°C to 55°C for most Indian locations.",
          "<strong>Mean Time Between Failures (MTBF):</strong> Higher is better; ask vendors for field data, not just datasheet numbers.",
          "<strong>Warranty and service network:</strong> On-site response SLA and spare parts availability in India.",
        ],
      },
      {
        type: "h2",
        text: "Top DC Fast Charger Vendors in India 2026",
      },
      {
        type: "table",
        headers: ["Vendor", "Power Range", "Connectors", "Approx. Price (60 kW)", "OCPP", "BIS Certified"],
        rows: [
          ["Tata Power EV", "30–120 kW", "CCS2 + CHAdeMO", "₹5.5L – ₹7L", "1.6J / 2.0", "Yes"],
          ["Delta Electronics", "30–200 kW", "CCS2 + CHAdeMO + AC", "₹5L – ₹6.5L", "1.6J / 2.0", "Yes"],
          ["Exicom", "30–60 kW", "CCS2 + CHAdeMO", "₹4.5L – ₹6L", "1.6J", "Yes"],
          ["ABB E-mobility", "50–350 kW", "CCS2 + CHAdeMO", "₹7L – ₹9L", "2.0.1", "Yes"],
          ["Servotech", "30–60 kW", "CCS2", "₹3.8L – ₹5L", "1.6J", "Yes"],
          ["Okaya EV", "30–60 kW", "CCS2", "₹3.5L – ₹4.8L", "1.6J", "Yes"],
          ["Siemens", "50–150 kW", "CCS2 + CHAdeMO", "₹8L – ₹12L", "2.0.1", "Yes"],
          ["Greaves Electric", "30–60 kW", "CCS2", "₹4L – ₹5.5L", "1.6J", "Yes"],
        ],
      },
      {
        type: "callout",
        content:
          "<strong>Prices are indicative (2026 market rates) and vary by order volume, warranty terms, and installation requirements.</strong> Always get at minimum 3 vendor quotes and evaluate total cost of ownership over 5 years, not just unit price.",
      },
      {
        type: "h2",
        text: "Vendor Profiles",
      },
      {
        type: "h3",
        text: "Tata Power EV",
      },
      {
        type: "p",
        content:
          "India's largest public charging network operator also manufactures chargers. Strong after-sales support with service centres across 25+ states. Their chargers are battle-tested at high-utilisation highway sites. Best choice if you want a vendor with deep India-specific operational knowledge.",
      },
      {
        type: "h3",
        text: "Delta Electronics",
      },
      {
        type: "p",
        content:
          "Taiwanese manufacturer with a strong India manufacturing base. Excellent build quality, wide power range, and OCPP 2.0 support on newer models. Good for operators who need 30–200 kW in the same product family. Slightly better MTBF data than domestic competitors.",
      },
      {
        type: "h3",
        text: "Exicom",
      },
      {
        type: "p",
        content:
          "Gurgaon-based manufacturer — the largest Indian-origin EV charger company. Cost-competitive pricing, BIS-certified, and strong distribution in North and West India. Good for budget-conscious projects where price is a key constraint and service network proximity matters.",
      },
      {
        type: "h3",
        text: "ABB E-mobility",
      },
      {
        type: "p",
        content:
          "Premium European brand with the most advanced OCPP 2.0.1 implementation and best-in-class uptime (99%+ reported). Highest upfront cost but lowest maintenance cost over 5 years for high-utilisation sites. Best suited for flagship commercial deployments.",
      },
      {
        type: "image-grid",
        images: [
          { src: "/chargers/tata.png",     label: "Tata Power EV",     sub: "30–120 kW · India's largest network" },
          { src: "/chargers/abb.jpg",      label: "ABB E-mobility",    sub: "50–350 kW · Premium uptime" },
          { src: "/chargers/delta.jpg",    label: "Delta Electronics",  sub: "30–200 kW · OCPP 2.0" },
          { src: "/chargers/exicom.jpg",   label: "Exicom",             sub: "30–60 kW · Cost-competitive" },
          { src: "/chargers/servotech.png", label: "Servotech",         sub: "30–60 kW · Budget-friendly" },
          { src: "/chargers/siemens.png",  label: "Siemens",            sub: "50–150 kW · Enterprise grade" },
        ],
      },
      {
        type: "h2",
        text: "When to Choose AC Over DC",
      },
      {
        type: "p",
        content:
          "DC fast chargers are not always the right choice. Use <strong>AC chargers (7.4–22 kW)</strong> when: dwell time at the location is 2+ hours (offices, hotels, residential), the vehicle mix includes two-wheelers and three-wheelers, your connected load is limited, or your CAPEX budget is constrained. Use <strong>DC fast chargers</strong> when: you're on a highway or intercity route, target dwell time is under 90 minutes, or you serve commercial fleets that need rapid turnaround.",
      },
      {
        type: "cta",
        text: "Compare all charger models, filter by power class, price, and connector type.",
        label: "Browse the Full Charger Catalog →",
        href: "/charger-catalog",
      },
    ],
  },

  {
    slug: "how-to-get-ev-charging-station-license-india",
    title: "How to Get an EV Charging Station License in India: Complete Compliance Guide",
    description:
      "There is no single 'EV charging station license' in India — instead, you need approvals from multiple authorities. This step-by-step guide covers DISCOM load sanction, BIS/AIS-138, fire NOC, BEE Evyatra registration, and state-specific requirements.",
    date: "2026-05-15",
    readTime: "10 min read",
    category: "Regulations & Compliance",
    tags: ["License", "Compliance", "DISCOM", "BEE Evyatra", "AIS-138", "India"],
    hero: {
      image: "/blog/blog-ev-charging-license.jpg",
      gradient: ["#1a2a3a", "#0d1820"],
      icon: "Shield",
      stat: "4 – 6",
      statLabel: "Approvals required",
      chips: ["DISCOM", "Fire NOC", "BEE Evyatra", "BIS/AIS-138"],
    },
    sections: [
      {
        type: "p",
        content:
          "One of the most common questions from first-time EV charging station developers in India is: <em>\"Where do I get the license?\"</em> The reality is that there is no single licence for EV charging stations in India. Instead, you need to obtain approvals from <strong>4–6 different regulatory bodies</strong>, and the specific requirements vary by state, station size, and charger type. This guide walks through every mandatory step.",
      },
      {
        type: "callout",
        content:
          "<strong>Important:</strong> As per the Ministry of Power order dated January 14, 2018 (amended 2022), EV charging is a <strong>service activity</strong> — not a distribution or generation activity. This means you do <em>not</em> need an electricity distribution licence, which significantly simplifies the process.",
      },
      {
        type: "h2",
        text: "Overview: Approvals Required",
      },
      {
        type: "table",
        headers: ["Approval", "Issuing Authority", "Mandatory?", "Typical Timeline"],
        rows: [
          ["DISCOM Load Sanction", "State DISCOM", "Yes — always", "4–12 weeks"],
          ["BIS/AIS-138 Equipment Certification", "BIS (via manufacturer)", "Yes — for equipment", "Pre-certified by vendor"],
          ["Fire NOC", "State Fire Department", "Yes — for stations > 500 sq ft or DC chargers", "2–8 weeks"],
          ["BEE Evyatra Registration", "Bureau of Energy Efficiency", "Yes", "1–2 weeks (online)"],
          ["Local Body Approval (building plan/NOC)", "Municipal Corp / Panchayat", "For new construction", "4–16 weeks"],
          ["Environmental Clearance", "State PCB", "Only for large stations > 20,000 sq m", "Rarely applicable"],
        ],
      },
      {
        type: "h2",
        text: "Step 1: DISCOM Load Sanction",
      },
      {
        type: "p",
        content:
          "The DISCOM (Distribution Company) connection is the critical path item — everything else can run in parallel, but without power, you have nothing. The process varies by state but generally follows:",
      },
      {
        type: "ol",
        items: [
          "Submit load application to your state DISCOM with site address, proposed connected load (kW), single-line diagram, and entity registration documents.",
          "DISCOM site inspection to assess feeder capacity and determine connection voltage (LT for < 50 kW, HT for ≥ 50 kW).",
          "Receive demand note for connection charges (₹2L–₹12L depending on load).",
          "Pay demand note and complete internal wiring as per DISCOM specification.",
          "Final inspection and meter installation.",
        ],
      },
      {
        type: "callout",
        content:
          "<strong>State tip:</strong> Karnataka (BESCOM), Maharashtra (MSEDCL), and Delhi (BSES/TPDDL) have EV-specific fast-track application windows that can cut timelines to 3–4 weeks. Always check your DISCOM's EV charging station portal first.",
      },
      {
        type: "h2",
        text: "Step 2: BIS/AIS-138 Equipment Certification",
      },
      {
        type: "p",
        content:
          "All EV charging equipment sold in India must comply with <strong>AIS-138 (Part 1 and Part 2)</strong> — the Indian standard for EV supply equipment, aligned with IEC 61851. As an operator, you don't need to certify equipment yourself — the <strong>manufacturer/vendor is responsible for BIS certification</strong>. Your obligation is to:",
      },
      {
        type: "ul",
        items: [
          "Verify BIS certification before purchasing (ask for the BIS certificate and registration number).",
          "Ensure equipment bears the BIS mark.",
          "Maintain documentation for inspection.",
          "Only purchase from vendors on the DHI/FAME-approved list if you're claiming subsidies.",
        ],
      },
      {
        type: "h2",
        text: "Step 3: Fire NOC",
      },
      {
        type: "p",
        content:
          "A Fire No Objection Certificate is required for any EV charging station with DC fast chargers or a total installed capacity exceeding the threshold set by your state fire department (typically 50 kW or 500 sq ft of covered area). The application is submitted to the state Fire and Emergency Services department with:",
      },
      {
        type: "ul",
        items: [
          "Site layout plan showing charger locations, fire extinguisher positions, and emergency exits.",
          "Electrical single-line diagram.",
          "Details of fire suppression and detection systems installed.",
          "Building plan approval (if applicable).",
        ],
      },
      {
        type: "h2",
        text: "Step 4: BEE Evyatra Registration",
      },
      {
        type: "p",
        content:
          "The <strong>BEE Evyatra portal</strong> is the national registry for all public EV charging stations. Registration is mandatory for all public-access chargers and is also a prerequisite for FAME III subsidy claims. Registration is online and straightforward:",
      },
      {
        type: "ol",
        items: [
          "Visit evyatra.beeindia.gov.in and create an operator account.",
          "Add each station with GPS coordinates, charger details (type, power, connector), and operating hours.",
          "Link your OCPP backend URL for real-time data reporting.",
          "Submit and receive your Evyatra station ID (typically within 5–10 business days).",
        ],
      },
      {
        type: "h2",
        text: "Step 5: State-Specific Requirements",
      },
      {
        type: "table",
        headers: ["State", "Additional Requirement"],
        rows: [
          ["Maharashtra", "Registration with MSEDCL EV portal; tariff category EVCS"],
          ["Karnataka", "BESCOM smart meter installation mandatory for DC stations"],
          ["Delhi", "Delhi EV cell registration; specific tariff category (EVCS) application"],
          ["Tamil Nadu", "TANGEDCO approval for HT connections; TEDA registration for solar integration"],
          ["Gujarat", "GUVNL/DGVCL coordination for highway stations; GERC tariff compliance"],
        ],
      },
      {
        type: "h2",
        text: "Common Mistakes to Avoid",
      },
      {
        type: "ol",
        items: [
          "<strong>Underestimating DISCOM timeline:</strong> The load sanction is almost always the longest step. Start this process first, before spending on other approvals.",
          "<strong>Buying non-BIS-certified equipment:</strong> Some vendors offer cheaper uncertified chargers. These cannot be used for FAME subsidy claims and create liability.",
          "<strong>Skipping OCPP backend setup:</strong> Evyatra and most state schemes require real-time data reporting. Plan for an OCPP backend subscription from day one.",
          "<strong>Not checking feeder capacity:</strong> A DISCOM connection doesn't guarantee stable power. Get the feeder loading data before selecting your site.",
          "<strong>Ignoring ToD tariff implications:</strong> Most DISCOMs now have Time-of-Day tariffs for EV charging. The rate differential (peak vs off-peak) significantly affects your revenue model.",
        ],
      },
      {
        type: "cta",
        text: "EV Hub Studio generates a full compliance checklist specific to your station location, charger configuration, and state — so you never miss an approval.",
        label: "Generate Your Compliance Checklist →",
        href: "/try",
      },
    ],
  },
];

export function getPostBySlug(slug) {
  return blogPosts.find((p) => p.slug === slug) || null;
}
